import { Box, Button, FormControlLabel, Popover, Typography } from '@mui/material';
import { ILinesVectorDisplay } from 'common/defines/clients';
import { HexColorPickerDebounce } from 'common/utils/HexColorPickerDebounce';
import { SwitchCustom } from 'components/Common/SwitchCustom';
import { useLayerSelectCardStyle } from 'components/MapView/MapViewStyle';
import { QUERY_KEY } from 'constants/constants';
import { FC, MouseEvent, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { updateLinesVectorColor } from 'services/analytics/apiAnalyticsConfig.services';
import { useAppDispatch } from 'store/hooks';
import { changeEachLinesVectorDisplay } from 'store/slices/rightBarSlice';

interface SwitchLinesVectorProps {
  item: ILinesVectorDisplay;
}

const SwitchLinesVector: FC<SwitchLinesVectorProps> = ({ item }) => {
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const classes = useLayerSelectCardStyle();

  const { _id, colorCode, lineCategory } = item;

  const onSuccessAction = () => {
    queryClient.invalidateQueries([QUERY_KEY.GET_LINES_VECTOR_LIST]);
    toast.success('Update color successful');
    handlePopoverClose();
  };

  const changeLinesVectorColorMutation = useMutation(updateLinesVectorColor, {
    onSuccess() {
      onSuccessAction();
    },
  });

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    event.preventDefault();
    event.stopPropagation();
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleChangeColor = () => {
    changeLinesVectorColorMutation.mutate({ _id, colorCode: selectedColor });
  };

  return (
    <Box>
      <FormControlLabel
        control={
          <SwitchCustom checked={item.visible} onClick={() => dispatch(changeEachLinesVectorDisplay({ _id }))} />
        }
        label={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}>
            {colorCode && (
              <Box
                sx={{
                  width: '14px',
                  height: '14px',
                  border: `1px solid ${colorCode}`,
                  borderRadius: '100%',
                  backgroundColor: colorCode,
                }}
                onClick={(e: any) => handleClick(e)}
              />
            )}
            <Typography className={classes.selectText} sx={{ ml: '6px' }}>
              {lineCategory}
            </Typography>
          </Box>
        }
      />
      <Popover
        id={'simple-popover'}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        sx={{
          '.MuiPopover-paper': {
            overflowX: 'initial',
            overflowY: 'initial',
          },
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        anchorOrigin={{
          vertical: 160,
          horizontal: 30,
        }}>
        <Box>
          <HexColorPickerDebounce
            style={{
              boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.2)',
              borderRadius: '10px',
            }}
            color={colorCode}
            onChange={(color: string) => {
              setSelectedColor(color);
            }}
          />
          <Box sx={{ my: '8px', textAlign: 'center' }}>
            <Button color="inherit" variant="outlined" size="small" onClick={handlePopoverClose}>
              Cancel
            </Button>
            <Button color="primary" variant="contained" size="small" sx={{ ml: '12px' }} onClick={handleChangeColor}>
              Save
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default SwitchLinesVector;
